import React, { useContext, useState } from 'react';
import {
    Badge,
    Button,
    Col,
    Container,
    Popover,
    PopoverHeader,
    PopoverBody,
    Row,
    Collapse,
    Spinner,
    Progress,
    UncontrolledDropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    Label,
} from 'reactstrap';
import { toast } from 'react-toastify';
import cls from 'classnames';
import { forEach, isEmpty } from 'lodash-es';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCaretRight,
    faCaretDown,
    faCaretUp,
    faCheckCircle,
    faExclamationTriangle,
    faInfoCircle,
    faQuestionCircle,
    faAngleDoubleLeft,
    faRotate,
    faEllipsisVertical,
} from '@fortawesome/free-solid-svg-icons';
import { Link } from '@s20/components/common/Link';
import { Hide, useIsMobile } from '@s20/components/common/Hide';
import { FormContext } from '@s20/components/forms/FormContext';
import { updateByPath } from '@s20/common/util/object';
import { reduceValue } from '@s20/common/util/select';
import CommonContext from '../../Common';
import { Can } from '../../Can';
import ValidationMessageDisplay from './ValidationMessageDisplay';
import { handleFormSaveError } from './ValidationError';
import './FormElements.scss';

export const ToastMessage = ({
    icon,
    header,
    message,
}) => (
    <div className="d-flex flex-column flex-nowrap">
        <div className="pb-2">
            <strong>
                <FontAwesomeIcon
                    className="mr-2"
                    size="lg"
                    icon={icon ?? faInfoCircle}
                />
                {header}
            </strong>
        </div>
        <div>{message}</div>
    </div>
);

export const ToastActionMessage = ({
    icon,
    header,
    message,
    buttonLabel,
    onActionClick,
}) => (
    <div className="d-flex flex-column flex-nowrap">
        <div className="pb-2 d-flex align-items-center">
            <FontAwesomeIcon
                className="mr-2"
                size="lg"
                icon={icon}
            />
            <strong>{header}</strong>
        </div>
        <div className="mb-2">{message}</div>
        <div>
            <button
                type="button"
                onClick={() => {
                    onActionClick();
                    toast.dismiss();
                }}
                style={{
                    background: 'transparent',
                    border: '1px solid #fff',
                    color: '#fff',
                    padding: '4px 8px',
                    borderRadius: '3px',
                    cursor: 'pointer',
                }}
            >
                {buttonLabel}
            </button>
        </div>
    </div>
);

/**
 * Generic handler for form fields.  Inputs must have the same name as the property on the object.
 * You must also bind the target method to your class.  Also requires that the name of the input
 * matches your object property hierarchy (ex. entity.prop1.prop2).
 *
 * Updated: RLC 11/2/20
 *
 * @param {any} e
 */
export function onFieldChange(e) { // eslint-disable-line
    const props = e.target.name.split('.');
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    this.setState((state) => {
        const newState = updateByPath({ ...state }, props, value);
        return newState;
    });
};

export function onYesNoChanged(value, meta) { // eslint-disable-line
    if (!meta?.name) {
        return;
    }

    const props = meta.name.split('.');

    this.setState((state) => {
        const newState = updateByPath({ ...state }, props, value);
        return newState;
    });
};

/**
 * Generic simple select handler for react select input.
 * You must also bind the target method to your class to change the {this} context.
 * Also requires that the name of the input matches your object property hierarchy
 *  (ex. entity.prop1.prop2). Supports multiselects.
 *
 * @param {any} selection
 */
export function onReactSelectChanged(selection, meta = {}) { // eslint-disable-line
    const { name } = meta;
    const path = name.split('.');
    const value = reduceValue(selection);

    this.setState((state) => {
        const newState = updateByPath({ ...state }, path, value);
        return newState;
    });
};

export const toasty = {
    success: (header, message) => {
        toast.dismiss();
        toast.success(
            <ToastMessage
                icon={faCheckCircle}
                header={header}
                message={message}
            />,
            { toastId: message },
        );
    },

    error: (header, message, options) => {
        toast.dismiss();
        toast.error(
            <ToastMessage
                icon={faExclamationTriangle}
                header={header}
                message={message}
            />,
            { ...(options ?? {}), toastId: message },
        );
    },

    info: (header, message) => {
        toast.dismiss();
        toast.error(
            <ToastMessage
                icon={faInfoCircle}
                header={header}
                message={message}
            />,
            { toastId: message },
        );
    },

    warning: (header, message, options) => {
        toast.dismiss();
        toast.warning(
            <ToastMessage
                icon={faInfoCircle}
                header={header}
                message={message}
            />,
            { ...(options ?? {}), toastId: message },
        );
    },

    action: (
        header,
        message,
        buttonLabel,
        onActionClick,
        onClose,
    ) => {
        toast.dismiss();
        toast.error(
            <ToastActionMessage
                icon={faInfoCircle}
                header={header}
                message={message}
                buttonLabel={buttonLabel}
                onActionClick={onActionClick}
            />,
            { autoClose: false, onClose },
        );
    },
};

export const CollapseUnderlineHeader = ({
    toggleCollapse,
    headerText,
    isOpen,
    className,
    children,
}) => (
    <>
        <Button
            className="collapse-header w-100 p-0"
            color="outline"
            onClick={toggleCollapse}
        >
            <span>{headerText}</span>
            <FontAwesomeIcon
                icon={isOpen ? faCaretUp : faCaretDown}
            />
        </Button>
        <Collapse
            isOpen={isOpen}
            timeout={50}
            className={cls(
                className ?? '',
                'pl-3 pr-1 site-collapser',
            )}
        >
            {children}
        </Collapse>
    </>
);

export const UncontrolledCollapseUnderlineHeader = ({
    defaultIsOpen = true,
    className,
    headerText,
    children,
}) => {
    const [isOpen, setIsOpen] = useState(defaultIsOpen);

    return (
        <CollapseUnderlineHeader
            headerText={headerText}
            isOpen={isOpen}
            toggleCollapse={() => setIsOpen(!isOpen)}
            className={className}
        >
            {children}
        </CollapseUnderlineHeader>
    );
};

export const CollapseUnderlineHeaderWithIcon = ({
    headerText,
    showIcon,
    onIconClick,
    isOpen,
    className,
    children,
}) => (
    <>
        <h4 style={{ justifyContent: 'start' }}>
            <span>{headerText}</span>
            {showIcon && (
                <span>
                    <button
                        style={{ paddingLeft: '2px' }}
                        type="button"
                        className="btn shadow-none"
                        onClick={onIconClick}
                    >
                        &nbsp;
                        <span className="mt-1">
                            <i
                                className="fa fa-plus-circle"
                                style={{ fontSize: '12px' }}
                            />
                        </span>
                    </button>
                </span>
            )}
        </h4>
        <Collapse
            isOpen={isOpen}
            timeout={50}
            className={cls(
                className ?? '',
                'pl-3 pr-1 site-collapser',
            )}
        >
            {children}
        </Collapse>
    </>
);

export const CollapseTableElement = ({
    toggleCollapse,
    isOpen,
    headerText,
    className,
    children,
}) => (
    <>
        <Button
            color="link"
            onClick={toggleCollapse}
        >
            &nbsp;
            <FontAwesomeIcon
                icon={isOpen ? faCaretDown : faCaretRight}
            />
            <span>{headerText}</span>
        </Button>
        <Collapse
            isOpen={isOpen}
            timeout={50}
            className={cls(
                className ?? '',
                'pl-3 pr-1 site-collapser',
            )}
        >
            {children}
        </Collapse>
    </>
);

export const FlexColumnStart = ({
    className,
    children,
    component: Component = 'div',
    ...restProps
}) => (
    <Component
        className={`d-flex flex-column justify-content-start ${className}`}
        {...restProps}
    >
        {children}
    </Component>
);

export const FlexColumnCenter = ({
    id,
    className,
    children,
}) => (
    <div
        id={id}
        className={`d-flex flex-column justify-content-center ${className ?? ''
            }`}
    >
        {children}
    </div>
);

export const FlexRow = ({
    className,
    children,
}) => (
    <div
        className={`d-flex flex-row align-items-center ${className}`}
    >
        {children}
    </div>
);

export const FlexBetweenRow = ({
    id,
    className,
    children,
}) => (
    <div
        id={id}
        className={`d-flex flex-row align-items-center justify-content-between ${className ?? ''
            }`}
    >
        {children}
    </div>
);

export const FlexCenterRow = ({
    id,
    className,
    children,
}) => (
    <div
        id={id}
        className={`d-flex flex-row align-items-center justify-content-center ${className ?? ''
            }`}
    >
        {children}
    </div>
);

export const FlexStartRow = ({
    id,
    className,
    children,
    component: Component = 'div',
    ...restProps
}) => (
    <Component
        id={id}
        className={`flex-fill d-flex flex-row align-items-center justify-content-start ${className ?? ''
            }`}
        {...restProps}
    >
        {children}
    </Component>
);

export const FlexEndRow = ({
    id,
    className,
    children,
    component: Component = 'div',
}) => (
    <Component
        id={id}
        className={`flex-fill d-flex flex-row align-items-center justify-content-end ${className ?? ''
            }`}
    >
        {children}
    </Component>
);

export const Flex = ({
    fill = true,
    row = true,
    alignItems = 'start',
    justifyContent = 'start',
    useColumnOnMobile = true,
    className = '',
    component: Component = 'div',
    ...restProps
}) => {
    const isMobile = useIsMobile();

    if (isMobile && useColumnOnMobile) {
        return (
            <Component
                className={cls(
                    'd-flex flex-column', className,
                    fill && 'flex-fill',
                )}
                {...restProps}
            />
        );
    }

    return (
        <Component
            className={cls(
                'd-flex', className, `align-items-${alignItems}`, `justify-content-${justifyContent}`,
                fill && 'flex-fill',
                row ? 'flex-row' : 'flex-column',
            )}
            {...restProps}
        />
    );
};

export const FormBlocker = (props) => {
    const marginTop = props.allowMenu ? '42px' : '0';
    return (
        <div
            style={{ marginTop }}
            className={cls(`formblocker ${props.className ?? ''}`, {
                show: !!props.show,
                'no-progress': !props.showProgress,
            })}
        >
            {!!props.showProgress && (
                <h1 className="formblocker-inner-progress">
                    <i className="fa fa-spin fa-circle-notch text-danger" />
                </h1>
            )}
        </div>
    );
};

export const FormCheckbox = ({
    className,
    id,
    name,
    checked,
    onChange = () => { },
    disabled,
    labelClass,
    small,
    labelText,
    children,
    innerRef,
}) => {
    const context = useContext(FormContext);

    let fieldChecked = checked || false;
    let fieldOnChange = onChange;

    if (context && context.values) {
        fieldChecked = context.values[name || id];
        fieldOnChange = (e, v) => {
            onChange(e, v);
            context.onChange(e, v);
        };

        // Cast "undefined" and "null" values to false
        if (!fieldChecked && fieldChecked !== false) {
            fieldChecked = false;
            context.setFieldValue(name || id, false);
        }
    }

    return (
        <div
            className={cls(
                'form-check mr-0',
                className,
            )}
        >
            <div>
                <input
                    id={`${id}-checkbox`}
                    name={name}
                    checked={fieldChecked}
                    type="checkbox"
                    onChange={fieldOnChange}
                    disabled={disabled}
                />

                <label
                    id={id}
                    htmlFor={`${id}-checkbox`}
                    disabled={disabled}
                    className={labelClass}
                    ref={innerRef}
                >
                    {small ? (
                        <small>
                            {labelText}
                        </small>
                    ) : (
                        <span>
                            {labelText}
                        </span>
                    )}
                </label>

                {children}
            </div>
        </div>
    );
};

export const FormCircularProgress = () => (
    <div className="d-flex flex-row h-100 align-items-center justify-content-center">
        <Spinner />
    </div>
);

export const FormDivider = () => (
    <Row className="m-0 pt-0">
        <Col>
            <hr />
        </Col>
    </Row>
);

export const FormGroupColumn = ({
    single,
    third,
    children,
}) => (
    <div
        className={cls({
            'col-xl-4 col-lg-4 col-md-4 col-sm-12': third,
            'col-xl-6 col-lg-6 col-md-6 col-sm-12': !single && !third,
        })}
    >
        {children}
    </div>
);

export class FormLabel extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hidden: false,
            helpOpen: false,
        };
    }

    toggle = () => this.setState((prevState) => ({ helpOpen: !prevState.helpOpen }));

    renderHelp() {
        return (this.props.helpMessage ?? '').length ? (
            <>
                <span
                    id={`${this.props.id}_help_popover`}
                    style={{ cursor: 'pointer', marginBottom: '.5rem' }}
                >
                    <FontAwesomeIcon
                        className="ml-2"
                        icon={faQuestionCircle}
                    />
                </span>
                <Popover
                    placement="right"
                    isOpen={this.state.helpOpen}
                    target={`${this.props.id}_help_popover`}
                    toggle={this.toggle}
                >
                    <PopoverHeader>{`${this.props.text || this.props.label} Help`}</PopoverHeader>
                    <PopoverBody>{this.props.helpMessage}</PopoverBody>
                </Popover>
            </>
        ) : (
            ''
        );
    }

    render() {
        const classNames = cls('control-label', this.props.className, {
            required: !!this.props.required,
        });

        return (
            <div className="d-flex flex-row flex-nowrap align-items-center">
                <Label
                    id={this.props.id}
                    className={classNames}
                    for={this.props.htmlFor}
                    hidden={this.state.hidden}
                    disabled={this.props.disabled}
                >
                    {this.props.text || this.props.label}
                </Label>

                {this.renderHelp()}
            </div>
        );
    }
}

/**
 * Form component that handles form validation errors.
 * Uses HTML5 checkValidity and toggles bootstrap styles
 * accordingly.
 * */
export class FormValidated extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            formData: {},
            saving: false,
            formInitialized: false,
            errors: {},
        };

        this.onChange = this.onChange.bind(this);
        this.submitHandler = this.submitHandler.bind(this);
        this.setFieldValue = this.setFieldValue.bind(this);
        this.setValues = this.setValues.bind(this);
    }

    componentDidMount() {
        const formData = new FormData(this.formEl);

        const state = Object.fromEntries(formData.entries() || []);

        if (!isEmpty(this.props.initialValues)) {
            forEach(this.props.initialValues, (value, key) => {
                state[key] = value;
            });
        }

        this.setState({ formData: state, formInitialized: true }); // eslint-disable-line
    }

    onChange = (e, field = null) => {
        let name = null;
        let value = null;

        if (e?.target) {
            name = e.target.name;
            value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;

            if (e.target.type === 'number') {
                value = parseFloat(value);
            }
        } else if (field?.action === 'select-option') {
            name = field.name;

            if (Array.isArray(e) || field.asObject) {
                value = e;
            } else if (e !== null && typeof e === 'object') {
                value = e[field.valueField] ?? e.value ?? e.id;
            } else {
                // ValidatedSelect with asPrimitive forwards the primitive value directly.
                value = e;
            }
        } else if (field?.action === 'clear') {
            name = field.name;
            value = null;
        } else if (field?.action === 'checkbox') {
            name = field.name;
            value = e;
        } else if (field?.action === 'remove-value') {
            const removed = field.removedValue;
            const removedValue = removed[field.valueField] ?? removed?.value ?? removed?.id;

            name = field.name;
            value = Array.isArray(e) ? e.filter((val) => val !== removedValue) : e;
        }

        if (name) {
            this.setState((prev) => ({
                ...prev,
                formData: {
                    ...prev.formData,
                    [name]: value,
                },
            }));
        }
    };

    // Wrap the handler and call it as provided if the form was valid.
    submitHandler = async (event) => {
        event.preventDefault();
        event.stopPropagation();

        this.setState({ saving: true });

        const valid = this.validate();

        if (valid) {
            try {
                await this.props.onSubmit(this.state.formData);
            } catch (error) {
                console.error({ error });
                handleFormSaveError(this, error);
            }
        }

        // isValid is an optional function that will be used to tell if the form was valid
        // during validation.
        if (typeof this.props.isValid === 'function') {
            this.props.isValid(valid);
        }

        this.props.setIsValidated(true);

        this.setState({ saving: false });
    };

    validate = () => {
        const { formEl } = this; // the form dom element.

        for (const elem of formEl) {
            // Handle react select controls.
            if (elem.classList.contains('react-select-validated-input')) {
                const form_group = elem.parentNode;
                const select = form_group.querySelector('.react-select');
                if (select) {
                    if (elem.validity.valid) {
                        select.classList.remove('is-invalid');
                        select.classList.add('is-valid');
                    } else {
                        select.classList.add('is-invalid');
                        select.classList.remove('is-valid');
                    }
                }
            }
        }

        const valid = formEl.checkValidity();

        return valid;
    };

    setFieldValue = (field, value) => {
        if (field) {
            this.setState((prev) => ({
                ...prev,
                formData: {
                    ...prev.formData,
                    [field]: value,
                },
            }));
        }
    };

    setValues = (values) => {
        this.setState({
            formData: values,
        });
    };

    render() {
        const classNames = cls(this.props.className, {
            'was-validated': !!this.props.isValidated,
        });

        return (
            <form
                id={this.props.id}
                className={cls('page-form', classNames)}
                noValidate
                ref={(form) => (this.formEl = form)}
                onSubmit={this.submitHandler} // take the submit handler defined by props.
                onKeyDown={(event) => {
                    // Prevent enter key submitting the form.
                    if (event.key === 'Enter' && event.shiftKey === false) {
                        event.preventDefault();
                    }
                }}
            >
                {this.state.formInitialized === true && (
                    <>
                        {typeof this.props.children === 'function'
                            ? (
                                <FormContext.Provider
                                    value={{
                                        values: this.state.formData,
                                        onSubmit: this.submitHandler,
                                        setFieldValue: this.setFieldValue,
                                        setValues: this.setValues,
                                        onChange: this.onChange,
                                        saving: this.state.saving,
                                        formId: this.props.id,
                                        errors: this.state.errors,
                                    }}
                                >
                                    {this.props.children({
                                        formId: this.props.id,
                                        submitHandler: this.submitHandler,
                                        values: this.state.formData,
                                        onChange: this.onChange,
                                        saving: this.state.saving,
                                        setFieldValue: this.setFieldValue,
                                        setValues: this.setValues,
                                        errors: this.state.errors,
                                    })}
                                </FormContext.Provider>
                            )
                            : this.props.children}
                    </>
                )}
            </form>
        );
    }
}

export const GroupedRow = ({
    children,
    className = '',
}) => <Row className={cls('grouped-row', className)}>{children}</Row>;


export const PageButtonToolbar = ({
    children,
}) => (
    <Row>
        <div className="d-flex flex-row align-items-center justify-content-end text-muted bottom-underline col mb-2 pb-1">
            {children}
        </div>
    </Row>
);

export const PageHeading = ({
    className,
    children,
}) => (
    <Row className="page-heading-row">
        <Col>
            <h5
                className={cls(
                    'w-100 bottom-underline mb-1 pb-2',
                    className,
                )}
            >
                {children}
            </h5>
        </Col>
    </Row>
);

export const PageWrap = ({
    id,
    children,
}) => (
    <div
        id={id ?? 'sitePageWrap'}
        className="d-flex flex-fill flex-column flex-nowrap p-3 site-page-wrap"
    >
        {children}
    </div>
);

export const SmallButton = ({
    className = '', children, selected = false, ...restProps
}) => {
    const classNames = cls('site-button-small', className);

    return (
        <Button
            color={selected ? 'primary' : 'outline-primary'}
            style={{ border: 'none', borderRadius: '0 !important' }}
            type="button"
            size="sm"
            className={classNames}
            {...restProps}
        >
            {children}
        </Button>
    );
};

export const IconButton = ({
    component: Component = Button, navigate, className = '', children, ...restProps
}) => {
    const classNames = cls('icon-button', className);

    return (
        <Component
            color="link"
            className={classNames}
            {...restProps}
        >
            {children}
        </Component>
    );
};

export const StyledButton = ({
    color = 'primary', outlined = false, outline = false, rounded = false,
    startIcon = null, endIcon = null, children, className = '',
    StartIconProps = {}, EndIconProps = {}, ...restProps
}) => {
    let classNames = cls('styled-button styled-button-contained', className, {
        's-btn-primary': color === 'primary',
        's-btn-secondary': color === 'secondary',
        's-btn-success': color === 'success',
        's-btn-danger': color === 'danger',
        's-btn-warning': color === 'warning',
        's-btn-info': color === 'info',
        's-btn-light': color === 'light',
        's-btn-dark': color === 'dark',
        'rounded-pill': Boolean(rounded),
        'icon-button': !children,
    });

    // Accept both props
    if (outlined || outline) {
        classNames = cls('styled-button styled-button-outlined', className, {
            's-btn-primary-outlined': color === 'primary',
            's-btn-secondary-outlined': color === 'secondary',
            's-btn-success-outlined': color === 'success',
            's-btn-danger-outlined': color === 'danger',
            's-btn-warning-outlined': color === 'warning',
            's-btn-info-outlined': color === 'info',
            's-btn-light-outlined': color === 'light',
            's-btn-dark-outlined': color === 'dark',
            'rounded-pill': Boolean(rounded),
            'icon-button': !children,
        });
    }

    return (
        <Button
            className={classNames}
            type="button"
            {...restProps}
            color="" // StyledButton handles button colour
            outline={false} // StyledButton handles button outline
        >
            {Boolean(startIcon) && (
                <FontAwesomeIcon
                    icon={startIcon}
                    {...StartIconProps}
                />
            )}

            {Boolean(children) && (
                <span>{children}</span>
            )}

            {Boolean(endIcon) && (
                <FontAwesomeIcon
                    icon={endIcon}
                    {...EndIconProps}
                />
            )}
        </Button>
    );
};

export const SmallPrimaryButton = (props) => {
    const classNames = cls('site-button-small', props.className);
    return (
        <Button
            color="primary"
            style={{ border: 'none', borderRadius: '0 !important' }}
            type="button"
            size="sm"
            className={classNames}
            {...props}
        >
            {props.children}
        </Button>
    );
};

export const SmallOutlineButton = (props) => {
    const classNames = cls('site-button-small', props.className);
    return (
        <Button
            type="button"
            size="sm"
            color="outline-primary"
            className={classNames}
            {...props}
        >
            {props.text ?? props.label ?? props.children}
        </Button>
    );
};

export const SubHeading = ({
    className,
    children,
    ...restProps
}) => {
    const classNames = cls('page-subheading mb-1 mt-0', className);
    return (
        <Row
            className={classNames}
            {...restProps}
        >
            <Col className="d-flex flex-row justify-content-center">
                <div className="h6 text-center p-1 m-0">
                    <span>
                        {children}
                    </span>
                </div>
            </Col>
        </Row>
    );
};

export const YesNoBadge = ({
    value,
}) => (
    <Badge
        color={value ? 'success' : 'secondary'}
        className="badge-yes-no"
    >
        {value ? 'YES' : 'NO'}
    </Badge>
);

export const SimpleTable = ({
    permission,
    leftToolbarContent,
    onAddFunction,
    addButtonEnabled,
    addLabelText,
    onRefreshFunction,
    refreshButtonEnabled,
    refreshLabelText,
    id,
    scrollable,
    className,
    tableHeaderLabels,
    editable,
    maxBodyHeight,
    entities,
    noDataText,
    rowRenderer,
}) => (
    <Can
        I="view"
        a={permission}
    >
        <div
            className="simple-table-container"
            id={id}
        >
            <FlexBetweenRow>
                <div className="flex-fill d-flex flex-row align-items-center justify-content-start">
                    {leftToolbarContent}
                </div>
                <div className="d-flex flex-row align-items-center justify-content-end">
                    {onRefreshFunction ? (
                        <Can
                            I="create"
                            a={permission}
                        >
                            <CommonContext.Consumer>
                                {(value) => (
                                    <SmallButton
                                        type="button"
                                        disabled={
                                            !!value.formIsOpen
                                            || refreshButtonEnabled
                                            === false
                                        }
                                        onClick={() => {
                                            onRefreshFunction();
                                        }}
                                    >
                                        <FontAwesomeIcon
                                            icon={faRotate}
                                            className="fa-md mr-2"
                                        />
                                        {
                                            refreshLabelText
                                        }
                                    </SmallButton>
                                )}
                            </CommonContext.Consumer>
                        </Can>
                    ) : (
                        ''
                    )}

                    {onAddFunction ? (
                        <Can
                            I="create"
                            a={permission}
                        >
                            <CommonContext.Consumer>
                                {(value) => (
                                    <SmallButton
                                        type="button"
                                        disabled={
                                            !!value.formIsOpen
                                            || addButtonEnabled
                                            === false
                                        }
                                        onClick={() => {
                                            onAddFunction();
                                        }}
                                    >
                                        <i className="fa fa-plus-circle fa-md mr-2" />
                                        {
                                            addLabelText
                                        }
                                    </SmallButton>
                                )}
                            </CommonContext.Consumer>
                        </Can>
                    ) : (
                        ''
                    )}
                </div>
            </FlexBetweenRow>

            <table
                className={cls(
                    'table table-sm',
                    {
                        'scrollable-table':
                            !!scrollable,
                    },
                    className,
                )}
            >
                <thead>
                    <tr key="headerRow">
                        {tableHeaderLabels.map(
                            (h) => (
                                <th
                                    key={h.name}
                                    className={h.class}
                                    style={{
                                        width:
                                            h.width ?? 'auto',
                                    }}
                                >
                                    {h.name}
                                </th>
                            ),
                        )}
                        {!!editable && (
                            <Can
                                I="edit"
                                a={permission}
                            >
                                <th
                                    className="text-center"
                                    style={{ width: '30px' }}
                                />
                            </Can>
                        )}
                    </tr>
                </thead>
                <tbody
                    style={{
                        maxHeight:
                            maxBodyHeight ?? 'auto',
                        overflowY: maxBodyHeight
                            ? 'scroll'
                            : 'unset',
                    }}
                >
                    {!(entities ?? []).length ? (
                        <tr key="emptyRow">
                            <td
                                colSpan={
                                    tableHeaderLabels
                                        .length
                                    + (editable
                                        ? 1
                                        : 0)
                                }
                                className="text-center"
                            >
                                {noDataText}
                            </td>
                        </tr>
                    ) : (
                        rowRenderer()
                    )}
                </tbody>
            </table>
        </div>
    </Can>
);

export class AppPageForm extends React.Component {
    renderHeader = () => {
        const { actions = [], headerRight } = this.props;

        return (
            <>

                <header className={cls('form-header', !isEmpty(actions) && 'sticky')}>
                    <PageHeading className="mt-3 d-flex align-items-end justify-content-between page-heading">
                        {this.props.backUrl ? (
                            <small>
                                <Link
                                    className="text-info text-right p-0"
                                    to={this.props.backUrl}
                                >
                                    <FontAwesomeIcon
                                        icon={faAngleDoubleLeft}
                                        className="mr-1"
                                    />
                                    <Hide mdUp>
                                        {this.props.backLabel ?? 'Back'}
                                    </Hide>

                                    <Hide smDown>
                                        Back
                                    </Hide>
                                </Link>
                            </small>
                        ) : <div />}

                        <div className="form-heading">
                            {Boolean(this.props.formHeadingIcon) && (
                                <FontAwesomeIcon
                                    className="mr-2 text-muted"
                                    icon={this.props.formHeadingIcon}
                                />
                            )}

                            {this.props.formHeading}
                        </div>

                        <div className="d-flex align-items-center">
                            {Boolean(headerRight) && headerRight}

                            {!isEmpty(actions) && (
                                <UncontrolledDropdown>
                                    <DropdownToggle
                                        tag="span"
                                        data-toggle="dropdown"
                                        role="menu"
                                    >
                                        <IconButton
                                            type="button"
                                            size="lg"
                                        >
                                            <FontAwesomeIcon
                                                icon={faEllipsisVertical}
                                                className="text-dark"
                                                size="lg"
                                            />
                                        </IconButton>
                                    </DropdownToggle>

                                    <DropdownMenu
                                        container="body"
                                        end
                                    >
                                        {actions.map((action) => {
                                            const {
                                                key, onClick, icon, label,
                                                className = '', CanProps = {},
                                                component: DropdownItemComponent = FlexStartRow,
                                                DropdownItemProps = {},
                                            } = action;

                                            return (
                                                <Can
                                                    key={key}
                                                    {...CanProps}
                                                    passThrough={isEmpty(CanProps)}
                                                >
                                                    <DropdownItemComponent
                                                        onClick={onClick}
                                                        component={DropdownItem}
                                                        className={cls('action-menu__action', className)}
                                                        {...DropdownItemProps}
                                                    >
                                                        <span>
                                                            <FontAwesomeIcon
                                                                className="mr-2"
                                                                icon={icon}
                                                            />
                                                        </span>

                                                        {label}
                                                    </DropdownItemComponent>
                                                </Can>
                                            );
                                        })}
                                    </DropdownMenu>
                                </UncontrolledDropdown>
                            )}
                        </div>
                    </PageHeading>
                </header>

                <Row>
                    <ValidationMessageDisplay
                        onClear={this.props.onClearErrors}
                        errors={this.props.errors}
                    />
                </Row>
            </>
        );
    };

    render() {
        const {
            viewOnly,
            saving,
            formShown,
            loading,
            parentClass,
            formId,
            formName,
            formIsColumn = false,
            initialValues = {},
        } = this.props;

        const isDisabled = Boolean(viewOnly || saving || formShown || loading);

        const formProps = {
            initialValues,
            className: cls('page-hidden col col-sm-12 col-lg-9', {
                ready: !loading,
                column: formIsColumn,
            }),
            id: formId,
            name: formName,
            ref: this.props.formRef,
            onSubmit: this.props.onSubmit,
            isValid: this.props.isValid,
            setIsValidated: this.props.setIsValidated,
            isValidated: this.props.isValidated,
        };

        return (
            <>
                <Progress
                    className={cls('m-0', { 'opacity-0': !saving })}
                    color="danger"
                />

                <fieldset
                    className={cls(
                        'app-page-form',
                        `${parentClass ?? ''
                        } flex-fill container opacity-1 position-relative mb-2`,
                        {
                            'overflow-y-hidden': loading,
                        },
                    )}
                    disabled={isDisabled}
                >
                    <FormBlocker
                        show={loading}
                        showProgress
                    />
                    <FlexCenterRow>
                        {typeof this.props.children === 'function' ? (
                            <FormValidated
                                {...formProps}
                            >
                                {(ctx) => (
                                    <Container
                                        fluid
                                        className={cls('m-0', formIsColumn && 'column')}
                                    >
                                        {this.renderHeader()}
                                        {this.props.children(ctx)}
                                    </Container>
                                )}
                            </FormValidated>
                        ) : (
                            <FormValidated
                                {...formProps}
                            >
                                <Container
                                    fluid
                                    className={cls('m-0', formIsColumn && 'column')}
                                >
                                    {this.renderHeader()}
                                    {this.props.children}
                                </Container>
                            </FormValidated>
                        )}
                    </FlexCenterRow>
                </fieldset>
            </>
        );
    }
}

export * from './Input';
export * from './NumericInput';
export * from './Textarea';
export * from './InfoCheckbox';
export { FormContext } from '@s20/components/forms/FormContext';